import { seoLinks, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import { GuideLinks } from "../components/site/Guides";
import { KnowledgeHubNote } from "../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../components/site/NewcomerNote";

const title = "Distansstudier på högstadiet och gymnasiet utomlands";
const description =
  "Så fungerar svenska distansstudier via Sofia Distans och Hermods med handledning på plats hos oss i Estepona – struktur, stöd och kombination med idrott.";

export const Route = createFileRoute("/distansstudier-hogstadiet-gymnasiet-utomlands")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/distansstudier-hogstadiet-gymnasiet-utomlands"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Distansstudier på högstadiet och gymnasiet"
        intro="Från årskurs 7 läser våra elever via svenska distansutbildningar, men gör det tillsammans i en skolmiljö med handledning. Här förklarar vi hur upplägget fungerar och vem det passar."
      />

      <NewcomerNote />

      <Prose>
        <h2>Två delar som hänger ihop</h2>
        <p>
          Distansstudier utomlands består av två saker. Den ena är utbildningen i sig: kursinnehåll,
          uppgifter, prov, bedömning och betyg kommer från en svensk distansskola – Sofia Distans för
          högstadiet och Hermods för gymnasiet. Den andra är vardagen: var eleven sitter, hur dagen
          struktureras, vilket stöd som finns och vilka den träffar. Den delen står vi för.
        </p>
        <p>
          Kombinationen är själva poängen. Distansutbildning som sköts ensam hemma vid köksbordet
          ställer mycket höga krav på självdisciplin och blir dessutom lätt ensam. Med en fast
          skoldag, handledning och andra elever i samma situation blir studieformen betydligt mer
          hållbar.
        </p>

        <h2>En vanlig skoldag</h2>
        <p>
          Eleven kommer till skolan på morgonen och arbetar med sina kurser enligt en planering.
          Handledaren följer upp hur arbetet går, hjälper till att strukturera veckan, stöttar när
          något kärvar och håller kontakt med distansskolan när det behövs. Dagen har start- och
          sluttider, raster och gemensamma moment – det ser mer ut som skola än som hemarbete.
        </p>
        <p>
          En stor fördel är att tempot kan anpassas. En elev som ligger före i ett ämne kan gå vidare
          utan att vänta in en klass, medan en elev som behöver mer tid får det utan att hamna
          efter i allt annat.
        </p>

        <h2>Betyg och behörighet</h2>
        <p>
          Eftersom kurserna läses inom det svenska systemet utfärdas betygen av distansskolan enligt
          svenska kursplaner. Det innebär att eleven bygger vidare på samma grund som
          klasskamraterna i Sverige och kan söka vidare till svensk gymnasieskola eller vidare
          studier på samma villkor. För familjer som räknar med att flytta hem är det ofta den
          avgörande fördelen jämfört med ett lokalt system.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="sv" />
      </div>

      <Prose>

        <h2>Att kombinera studier med idrott</h2>
        <p>
          Distansformen gör schemat flexibelt, vilket är förutsättningen för att kunna kombinera
          skolan med seriös träning. Elever i{" "}
          <Link className="text-primary link-quiet" to="/fotbollsakademi">
            Malmö FF:s fotbollsakademi
          </Link>{" "}
          och i{" "}
          <Link className="text-primary link-quiet" to="/padel-tennis">
            padel- och tennisprofilen
          </Link>{" "}
          lägger studietiden runt träningspassen i stället för tvärtom. Klimatet gör att träningen
          kan ske utomhus året runt, vilket ger fler kvalitativa pass än en normal svensk säsong.
        </p>

        <h2>Vem passar det för?</h2>
        <p>
          Upplägget fungerar bäst för elever som klarar att arbeta självständigt under en dag och
          som tar emot stöd när de fastnar. Motivation väger tyngre än betyg – en elev som vill men
          behöver struktur klarar sig oftast bra, medan en elev som helst undviker skolarbete får det
          tuffare i en form som bygger på eget ansvar.
        </p>
        <p>
          Vi är öppna med det i samtalen inför en start. Det är bättre att prata igenom
          förutsättningarna innan än att upptäcka halvvägs in i en termin att formen inte passar. Vid
          behov av extra stöd tittar vi tillsammans på vad som är rimligt att erbjuda; läs mer om vårt
          arbete med{" "}
          <Link className="text-primary link-quiet" to="/extra-stod">
            elever i behov av extra stöd
          </Link>
          .
        </p>

        <h2>Vad föräldrar brukar fråga om</h2>
        <ul>
          <li>
            <strong>Blir eleven ensam?</strong> Nej – studierna sker i grupp på skolan, med andra
            elever och vuxna omkring sig.
          </li>
          <li>
            <strong>Hur följer vi som föräldrar studierna?</strong> Genom distansskolans system och
            genom löpande kontakt med handledaren.
          </li>
          <li>
            <strong>Går det att börja mitt i terminen?</strong> Ja, ansökningar tas emot löpande.
          </li>
          <li>
            <strong>Vad kostar det?</strong> Se{" "}
            <Link className="text-primary link-quiet" to="/avgifter">
              avgifter
            </Link>{" "}
            för aktuella nivåer.
          </li>
        </ul>

        <h2>Nästa steg</h2>
        <p>
          Läs mer om upplägget på sidan{" "}
          <Link
            className="text-primary link-quiet"
            to="/hogstadium-gymnasium"
          >
            högstadium och gymnasium
          </Link>
          , eller gör en ansökan direkt. Vi återkommer med besked om plats och går igenom vad som
          gäller för just er elev.
        </p>
      </Prose>

      <KnowledgeHubNote resource="flyttchecklistan" />

      <GuideLinks current="/distansstudier-hogstadiet-gymnasiet-utomlands" />
      <CtaBand />
      <div className="h-20" />
    </>
  );
}
